import { useEffect, useState } from "react";
import Box from "../components/ui/Box";
import BreadCrumb from "../components/BreadCrumb";
import CustomButton from "../components/CustomButton";
import { Header } from "./Home";
import { FaRegCalendarCheck } from "react-icons/fa";
import { useUser } from "../contexts/UserContextProvider";
import { getReminders, deleteReminder, createReminder } from "../api/Api";
import { showToast } from "../utils/toastUtils";


// Helper to calculate days between two dates (ignoring time)
function daysBetween(date1, date2) {
  const d1 = new Date(Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate()));
  const d2 = new Date(Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate()));
  return Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
}

const ReminderCard = ({ conf, reminder, onRemove }) => {
  // Reconstruct reminder date from deadline and value (days before deadline)
  const deadlineDate = new Date(reminder.submission_deadline);
  let reminderDate;
  let invalidReminder = false;
  if (reminder.value) {
    reminderDate = new Date(deadlineDate);
    reminderDate.setDate(deadlineDate.getDate() - parseInt(reminder.value, 10));
    // If reminder date is after deadline, mark as invalid
    if (reminderDate > deadlineDate) invalidReminder = true;
  } else {
    reminderDate = deadlineDate;
  }

  // Days until deadline and reminder
  const today = new Date();
  const daysUntilDeadline = daysBetween(today, deadlineDate);
  const daysUntilReminder = daysBetween(today, reminderDate);

  // Helper to format date as YYYY-MM-DD, or 'Now' if today
  const formatDate = (dateObj) => {
    if (!dateObj) return '';
    const today = new Date();
    if (
      dateObj.getFullYear() === today.getFullYear() &&
      dateObj.getMonth() === today.getMonth() &&
      dateObj.getDate() === today.getDate()
    ) {
      return 'Now';
    }
    return dateObj.toISOString().slice(0, 10);
  };

  // Helper to get display text for the relative time
  const getDisplayRelative = (days) => {
    if (days === 0) return 'Now';
    if (days > 0) return `in ${days} day${days > 1 ? 's' : ''}`;
    return 'Expired';
  };

  return (
    <div
      className="d-flex p-3 column-gap-2 w-100 rounded-4 secondary-box align-items-center"
    >
      {/* Icon */}
      <div className="px-2 d-flex" style={{ height: "max-content" }}>
        <div
          className="rounded-5 d-flex justify-content-center align-items-center"
          style={{ width: "50px", height: "50px", backgroundColor: "#0bd5fe" }}
        >
          <FaRegCalendarCheck size={25} className="text-white" />
        </div>
      </div>
      {/* Info */}
      <div className="d-flex flex-column w-100 row-gap-2">
        <div className="d-flex w-100 justify-content-between">
          <span className="p4 fw-semibold">{conf.title}</span>
        </div>
        <div className="d-flex w-100 justify-content-between align-items-end">
          <div className="d-flex flex-column">
            <span className="text-secondary p5">
              Submission Deadline: {formatDate(deadlineDate)} ({getDisplayRelative(daysUntilDeadline)})
            </span>
            <span className="text-secondary p5">
              Reminder will notify you on: {formatDate(reminderDate)} ({getDisplayRelative(daysUntilReminder)})
            </span>
            {invalidReminder ? (
              <span className="p6 text-danger">
                Invalid reminder date (after deadline)
              </span>
            ) : daysUntilReminder === 0 ? (
              <span className="p6" style={{ color: '#2563eb', fontWeight: 600 }}>
                Action required now!
              </span>
            ) : daysUntilReminder < 0 ? (
              <span className="p6 text-danger">
                This reminder has expired
              </span>
            ) : null}
          </div>
          <div className="d-flex gap-2">
            <CustomButton
              variant="outline"
              className="py-2 p4"
              onClick={() => window.open(reminder.conference_url, '_blank')}
            >
              View
            </CustomButton>
            <CustomButton variant="outline" className="py-2 p4" onClick={onRemove}>
              Remove
            </CustomButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const RemindersPage = () => {
  const { userInfo } = useUser();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);

  // Function to send reminder value to backend
  const sendReminderValue = async (reminderData) => {
    if (!userInfo?.id) {
      showToast("User not authenticated", "error");
      return;
    }

    setCalculating(true);
    try {
      let valueToSend;
      let customDate;
      let deadlineDate;
      if (reminderData.type === "custom") {
        customDate = new Date(reminderData.value);
        deadlineDate = new Date(reminderData.submission_deadline);
        const diffTime = customDate.getTime() - deadlineDate.getTime();
        if (customDate > deadlineDate) {
          showToast("Reminder date cannot be after the submission deadline", "error");
          setCalculating(false);
          return;
        }
        const diffDays = daysBetween(customDate, deadlineDate);
        valueToSend = diffDays.toString(); // Send number of days as string
      } else {
        // For preset options, send the number directly
        valueToSend = reminderData.type;
      }

      // Send only the value to backend
      const payload = {
        user_id: userInfo.id,
        value: valueToSend
      };

      // Send to backend
      const response = await createReminder(payload);
      
      if (response?.data) {
        showToast("Reminder set successfully", "success");
        // Refresh the reminders list
        loadReminders();
      } else {
        showToast("Failed to create reminder", "error");
      }

    } catch (error) {
      console.error("Error sending reminder value:", error);
      showToast("Error creating reminder", "error");
    } finally {
      setCalculating(false);
    }
  };

  // Function to load reminders (extracted from useEffect for reuse)
  const loadReminders = async () => {
    if (!userInfo?.id) return;
    setLoading(true);
    try {
      const response = await getReminders(userInfo.id);
      if (response?.data) {
        setReminders(response.data);
      }
    } catch (e) {
      console.error("Error fetching reminders:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReminders();
  }, [userInfo]);

  const handleRemove = async (id) => {
    try {
      await deleteReminder(id);
      setReminders(reminders.filter(r => r.id !== id));
      showToast("Reminder removed successfully", "success");
    } catch (e) {
      console.error("Error removing reminder:", e);
    }
  };

  return (
    <Box>
      <BreadCrumb
        array={[
          { title: "Confernce recommender",link:"/conference-recommender" },
          { title: "Reminders" }
                  
        ]}
        icon={<FaRegCalendarCheck size={24} color="#0bd5fe" />}
      />
      <div className="bg-white">
        <Box innerClass="primary-box">
          <Header title="All Reminders" showButton={false} />
          <Box>
            {loading ? (
              <div className="text-center text-muted p4 py-5">Loading...</div>
            ) : reminders.length === 0 ? (
              <div className="text-center text-muted p4 py-5">
                <p>No reminders set yet. You're all caught up!</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {reminders.map((reminder) => (
                  <ReminderCard
                    key={reminder.id}
                    conf={{
                      title: reminder.conference_title,
                      id: reminder.conference_id
                    }}
                    reminder={reminder}
                    onRemove={() => handleRemove(reminder.id)}
                  />
                ))}
              </div>
            )}
          </Box>
        </Box>
      </div>
    </Box>
  );
};

export default RemindersPage; 